import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { AlertTriangle, Trash2 } from 'lucide-react';

interface AdminDismissUserPanelProps {
  userId: string;
  userName: string;
  userEmail: string;
  hasStripeSubscription: boolean;
  onDismissed: () => void;
}

export function AdminDismissUserPanel({
  userId,
  userName,
  userEmail,
  hasStripeSubscription,
  onDismissed,
}: AdminDismissUserPanelProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [cancelStripe, setCancelStripe] = useState(true);
  const [gdprAck, setGdprAck] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep(1);
    setConfirmEmail('');
    setCancelStripe(true);
    setGdprAck(false);
  };

  const handleDismiss = async () => {
    if (confirmEmail.trim().toLowerCase() !== userEmail.toLowerCase()) {
      toast.error('El correo no coincide');
      return;
    }
    if (!gdprAck) {
      toast.error('Debes confirmar la solicitud de eliminación');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-delete-user', {
        body: {
          userId,
          confirmEmail: confirmEmail.trim(),
          cancelStripeSubscription: cancelStripe,
          acknowledgedGdprRequest: true,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(data.message ?? 'Usuario desestimado');
      setOpen(false);
      reset();
      onDismissed();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al desestimar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="border-destructive/30 bg-destructive/[0.02]">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Zona de desestimiento
          </CardTitle>
          <CardDescription>
            Elimina permanentemente la cuenta y todos los datos del usuario (derecho al olvido).
            Esta acción no se puede deshacer.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="destructive"
            className="gap-2"
            onClick={() => {
              reset();
              setOpen(true);
            }}
          >
            <Trash2 className="h-4 w-4" />
            Desestimar usuario
          </Button>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) reset();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {step === 1 ? '¿Desestimar esta cuenta?' : 'Confirmar eliminación'}
            </DialogTitle>
            <DialogDescription>
              {userName} · {userEmail}
            </DialogDescription>
          </DialogHeader>

          {step === 1 ? (
            <div className="space-y-3 py-2 text-sm">
              <p>Se eliminará de forma irreversible:</p>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li>Credenciales y perfil</li>
                <li>Transacciones, metas, deudas y presupuestos</li>
                <li>Progreso FOCO e historial de chat</li>
                <li>Avatar en almacenamiento</li>
                {hasStripeSubscription && (
                  <li>Suscripción activa en Stripe (si confirmas abajo)</li>
                )}
              </ul>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="confirm-email">
                  Escribe <span className="font-mono text-xs">{userEmail}</span> para confirmar
                </Label>
                <Input
                  id="confirm-email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={userEmail}
                  autoComplete="off"
                />
              </div>

              {hasStripeSubscription && (
                <label className="flex items-start gap-3 text-sm">
                  <Checkbox
                    checked={cancelStripe}
                    onCheckedChange={(c) => setCancelStripe(c === true)}
                    className="mt-0.5"
                  />
                  <span>Cancelar suscripción en Stripe antes de eliminar</span>
                </label>
              )}

              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  checked={gdprAck}
                  onCheckedChange={(c) => setGdprAck(c === true)}
                  className="mt-0.5"
                />
                <span>
                  Confirmo que el usuario solicitó la eliminación de sus datos (derecho al olvido)
                </span>
              </label>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            {step === 1 ? (
              <Button variant="destructive" onClick={() => setStep(2)}>
                Continuar
              </Button>
            ) : (
              <Button
                variant="destructive"
                disabled={
                  loading ||
                  !gdprAck ||
                  confirmEmail.trim().toLowerCase() !== userEmail.toLowerCase()
                }
                onClick={handleDismiss}
              >
                {loading ? 'Eliminando…' : 'Eliminar permanentemente'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
